import React, { useState, useEffect, useRef } from "react";

// Module-scoped flag: the "hello" reveal fires once per hard-reload session.
// Client-side navigation within the SPA won't replay it — that's the goal.
let hasPlayedHello = false;
// A shared AudioContext, lazily created on the first user gesture so browsers
// never block us. Reused for the lifetime of the tab.
let sharedAC = null;

function playWhoosh() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!sharedAC) sharedAC = new AC();
    const ctx = sharedAC;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;

    // Sine tone sliding 340Hz → 180Hz over 350ms with a lowpass shelter.
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = "sine";
    osc.frequency.setValueAtTime(340, now);
    osc.frequency.exponentialRampToValueAtTime(180, now + 0.32);

    filter.type = "lowpass";
    filter.frequency.setValueAtTime(1600, now);
    filter.Q.setValueAtTime(0.7, now);

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.055, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.36);
  } catch { /* ignore audio errors (autoplay policies, no AC support, etc.) */ }
}

/**
 * Wikifi header lockup — the new minimalist mark.
 * • Sleek "wikifi" wordmark (bold black lowercase) with three coloured dots
 *   above the three "i" letters (red / teal / blue).
 * • Below: "EVERY AI" tagline in tight small-caps.
 * • First page load: subtle fade-in on the tagline. Hover replays the
 *   Wiki whoosh tone for continuity with the previous brand.
 */
export const Logo = ({ className = "" }) => {
  const [playHello, setPlayHello] = useState(() => !hasPlayedHello);
  const hoverCooldown = useRef(0);

  useEffect(() => {
    if (hasPlayedHello) return;
    hasPlayedHello = true;
    const t = setTimeout(() => setPlayHello(false), 2600);
    return () => clearTimeout(t);
  }, []);

  const onHover = () => {
    const now = Date.now();
    if (now - hoverCooldown.current < 900) return;
    hoverCooldown.current = now;
    playWhoosh();
  };

  return (
    <div
      className={`inline-flex items-center leading-none ${className}`}
      data-testid="wikifi-logo"
      onMouseEnter={onHover}
    >
      <span
        className={`font-display font-bold text-[#0B1220] text-[22px] md:text-[24px] leading-none tracking-tight select-none ${playHello ? "wiki-logo-enter" : ""}`}
        style={{ letterSpacing: "-0.03em" }}
      >
        Wikificial
      </span>
    </div>
  );
};

/** Icon-only compact lockup for tight spaces (share cards, meta). */
export const LogoMark = () => (
  <div className="inline-flex items-center gap-2" data-testid="wikifi-logomark">
    <span className="font-display font-bold text-[#0B1220] text-[15px] tracking-tight" style={{ letterSpacing: "-0.03em" }}>Wikificial</span>
  </div>
);
